import { SkFont } from "@shopify/react-native-skia";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import ComparePlayersChart from "@/features/compare-players/components/organisms/ComparePlayersChart";

/**
 *
 */
interface Data extends Record<string, unknown> {
  [x: string]: string | number;
  season: string | number;
}

/**
 *
 */
interface Props {
  title: string;
  data?: Data[];
  yKeys?: string[];
  colors?: Record<string, string>;
  currentPlayerName: string;
  selectedPlayerName: string;
  font?: SkFont | null;
  labelColor?: string;
  formatXLabel: (value: string | number) => string;
}

/**
 * View component for rendering player comparison chart with legend
 * Displays line chart and color-coded legend for both players
 * @param root0 - Component props
 * @param root0.title - Chart title displayed above the chart
 * @param root0.data - Array of data points to plot on the chart
 * @param root0.yKeys - Array of metric keys for y-axis values
 * @param root0.colors - Color mapping for each player's metric line
 * @param root0.currentPlayerName - Name of primary player for legend
 * @param root0.selectedPlayerName - Name of comparison player for legend
 * @param root0.font - Skia font for rendering chart text
 * @param root0.labelColor - Color for chart axis labels (theme-based)
 * @param root0.formatXLabel - Function to format x-axis labels (handles mobile/desktop)
 */
const ChartComparisonView = ({
  title,
  data = [],
  yKeys = [],
  colors = {},
  currentPlayerName,
  selectedPlayerName,
  font,
  labelColor,
  formatXLabel,
}: Props) =>
  !!data.length && (
    <Stack className="gap-1 rounded-none border-2 border-surface-action bg-surface-base p-4">
      <Heading variant="title" className="text-title">
        {title}
      </Heading>
      <ComparePlayersChart
        data={data}
        xKey="season"
        yKeys={yKeys}
        colors={colors}
        font={font}
        width={600}
        height={384}
        domainPadding={20}
        padding={50}
        xAxis={{
          labelColor,
          formatXLabel,
        }}
        yAxis={[{ labelColor }]}
      />
      <Stack direction="horizontal" className="flex-1 items-center gap-4 pt-8">
        <Stack direction="horizontal" className="shrink items-center gap-2">
          <Box className="size-4 rounded-none bg-chart-player" />
          <Text variant="unstyled" className="shrink">
            {currentPlayerName}
          </Text>
        </Stack>
        {!!selectedPlayerName && (
          <Stack direction="horizontal" className="shrink items-center gap-2">
            <Box className="size-4 rounded-none bg-chart-other" />
            <Text variant="unstyled" className="shrink">
              {selectedPlayerName}
            </Text>
          </Stack>
        )}
      </Stack>
    </Stack>
  );

ChartComparisonView.displayName = "ChartComparisonView";

export default memo(ChartComparisonView);
